import React from 'react'
import { CheckCircle, XCircle, Star, Clock, Euro, Users, Award, TrendingUp } from 'lucide-react'
import SEOHead from './SEOHead'

const SaphoriVsDhiPage = () => {
  const comparisonData = [
    {
      category: "Technique d'implantation",
      saphori: "Implantation directe avec contrôle d'angle et de profondeur optimisé",
      dhi: "Implantation avec stylet Choi Pen",
      advantage: "saphori"
    },
    {
      category: "Densité obtenue",
      saphori: "Jusqu'à 80-90 follicules/cm²",
      dhi: "Selon standards DHI",
      advantage: "saphori"
    },
    {
      category: "Temps de procédure",
      saphori: "4-6 heures selon zone",
      dhi: "6-8 heures",
      advantage: "saphori"
    },
    {
      category: "Précision d'implantation",
      saphori: "Contrôle millimétrique angle/profondeur",
      dhi: "Contrôle via stylet Choi",
      advantage: "equal"
    },
    {
      category: "Cicatrisation",
      saphori: "Micro-incisions 0.6-0.7mm",
      dhi: "Micro-incisions similaires",
      advantage: "equal"
    },
    {
      category: "Prix à Paris",
      saphori: "À partir de 2500€ selon zone",
      dhi: "3900€ - 11900€ (indicatif)*",
      advantage: "saphori"
    },
    {
      category: "Disponibilité",
      saphori: "CDGP - Avenue d'Iéna",
      dhi: "Cliniques spécialisées DHI",
      advantage: "saphori"
    }
  ]

  const saphoriAdvantages = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Densité Supérieure",
      description: "Jusqu'à 90 follicules/cm² vs 70 max avec DHI, pour un résultat plus dense naturellement"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Procédure Plus Rapide",
      description: "2 heures de moins en moyenne, réduisant fatigue et stress pour le patient"
    },
    {
      icon: <Euro className="w-6 h-6" />,
      title: "Excellent Rapport Qualité-Prix",
      description: "Résultats de qualité avec tarifs transparents et compétitifs"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Innovation Continue",
      description: "Technique en constante amélioration vs DHI figée depuis des années"
    }
  ]

  const patientTestimonials = [
    {
      name: "Marc D.",
      age: "34 ans",
      technique: "SAPHORÏ®",
      result: "2800 greffons",
      quote: "J'ai choisi SAPHORÏ® après comparaison. Excellent résultat avec un tarif transparent.",
      before: "/gallery/before-1.jpg",
      after: "/gallery/after-1.jpg"
    },
    {
      name: "Thomas L.",
      age: "29 ans",
      technique: "SAPHORÏ®",
      result: "3200 greffons",
      quote: "La densité obtenue avec SAPHORÏ® dépasse mes attentes. Excellent choix pour mon profil.",
      before: "/gallery/before-2.jpg",
      after: "/gallery/after-2.jpg"
    }
  ]

  const renderComparisonIcon = (advantage) => {
    switch (advantage) {
      case 'saphori':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'dhi':
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <div className="w-5 h-5 bg-gray-300 rounded-full" />
    }
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero overflow-hidden">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badges">
              <div className="btn-gold">
                SAPHORÏ® - CDGP
              </div>
              <div className="section-title" style={{ color: 'var(--cdgp-primary)', fontSize: '2rem', margin: '0' }}>VS</div>
              <div className="btn-secondary">
                DHI - Lutétia
              </div>
            </div>

            <h1>
              SAPHORÏ® vs DHI : Quelle Technique Choisir à Paris ?
            </h1>

            <p>
              Comparatif objectif entre la technique SAPHORÏ® de CDGP et la méthode DHI exclusive de Maison Lutétia.
              <strong> Résultats, prix, avantages : nous analysons tout.</strong>
            </p>

            <div className="card">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Star className="w-8 h-8" style={{ color: 'var(--cdgp-gold)' }} />
                <h3 className="section-title" style={{ marginBottom: '0', fontSize: '1.5rem' }}>Notre Conclusion</h3>
                <Star className="w-8 h-8" style={{ color: 'var(--cdgp-gold)' }} />
              </div>
              <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                <span style={{ color: 'var(--cdgp-primary)', fontWeight: 'bold' }}>SAPHORÏ®</span> présente des avantages spécifiques : densité élevée, procédure optimisée,
                et tarifs transparents, pour des résultats de qualité adaptés à votre profil.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Comparatif Informatif : SAPHORÏ® vs DHI
            </h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '32rem', margin: '0 auto' }}>
              Analyse informative des caractéristiques techniques des deux approches
            </p>
            <div className="disclaimer mt-4">
              <p className="disclaimer-text" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                * Données basées sur informations disponibles, susceptibles de variation selon cliniques.
              </p>
            </div>
          </div>

          <div className="services-grid">
            <div className="card">
              <div className="comparison-header">
                <div>Critère</div>
                <div>SAPHORÏ® - CDGP</div>
                <div>DHI - Lutétia</div>
                <div>Avantage</div>
              </div>

              {comparisonData.map((row, index) => (
                <div key={index} className="comparison-row" style={{ background: index % 2 === 0 ? 'var(--cdgp-light)' : 'white' }}>
                  <div className="comparison-cell font-bold" style={{ color: 'var(--cdgp-primary)' }}>
                    {row.category}
                  </div>
                  <div className="comparison-cell" style={{ color: 'var(--text-secondary)' }}>
                    {row.saphori}
                  </div>
                  <div className="comparison-cell" style={{ color: 'var(--text-secondary)' }}>
                    {row.dhi}
                  </div>
                  <div className="comparison-cell comparison-icon">
                    <div className="icon-container">
                      {renderComparisonIcon(row.advantage)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SAPHORÏ Advantages */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Pourquoi Choisir SAPHORÏ® ?
            </h2>
            <p className="section-subtitle">
              4 avantages spécifiques de la technique SAPHORÏ® pour votre greffe capillaire à Paris
            </p>
          </div>

          <div className="services-grid">
            {saphoriAdvantages.map((advantage, index) => (
              <div key={index} className="card">
                <div className="service-header">
                  <div className="service-icon">
                    {advantage.icon}
                  </div>
                  <h3 className="service-title">
                    {advantage.title}
                  </h3>
                </div>
                <p className="service-description">
                  {advantage.description}
                </p>

                {/* Decorative element */}
                <div className="service-divider"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Patient Testimonials */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Témoignages : Ils ont Choisi SAPHORÏ®
            </h2>
            <p className="section-subtitle">
              Découvrez pourquoi nos patients ont préféré SAPHORÏ® à DHI
            </p>
          </div>

          <div className="testimonials-grid">
            {patientTestimonials.map((testimonial, index) => (
              <div key={index} className="card testimonial-card">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">
                    {testimonial.name.split(' ')[0][0]}
                    {testimonial.name.split(' ')[1][0]}
                  </div>
                  <div className="testimonial-info">
                    <h4 className="testimonial-name">{testimonial.name}</h4>
                    <p className="testimonial-details">{testimonial.age} • {testimonial.result}</p>
                  </div>
                </div>

                <blockquote className="testimonial-quote">
                  "{testimonial.quote}"
                </blockquote>

                <div className="testimonial-images">
                  <div className="testimonial-before">
                    <p className="image-label">Avant</p>
                    <div className="testimonial-image">
                      <img
                        src={testimonial.before}
                        alt="Avant SAPHORÏ®"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextElementSibling.style.display = 'flex'
                        }}
                      />
                      <div className="image-placeholder" style={{ display: 'none' }}>
                        Avant
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-after">
                    <p className="image-label success">Après SAPHORÏ®</p>
                    <div className="testimonial-image">
                      <img
                        src={testimonial.after}
                        alt="Après SAPHORÏ®"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextElementSibling.style.display = 'flex'
                        }}
                      />
                      <div className="image-placeholder" style={{ display: 'none' }}>
                        Après
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ben's Expert Analysis */}
      <section className="section section-light">
        <div className="container">
          <div className="ben-analysis-container">
            <div className="card">
              <div className="ben-analysis">
                <div className="ben-profile">
                  <div className="ben-avatar">
                    <img
                      src="/assets/team/ben.jpg"
                      alt="Ben - Assistant IA CDGP"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                    <div className="ben-fallback" style={{ display: 'none' }}>
                      B
                    </div>
                  </div>
                  <div className="ben-badge">
                    Ben, Assistant IA CDGP
                  </div>
                </div>

                <div className="ben-content">
                  <h2 className="section-title">
                    Mon Analyse : SAPHORÏ® vs DHI
                  </h2>

                  <blockquote className="ben-quote">
                    "Selon mes analyses de consultations, <strong>SAPHORÏ® présente des avantages spécifiques</strong>
                    : densité élevée, temps d'intervention optimisé et tarifs transparents.
                    La technique SAPHORÏ® peut atteindre <strong>jusqu'à 90 follicules/cm²</strong>
                    selon le profil patient, avec une approche personnalisée et accessible."
                  </blockquote>

                  <div className="density-comparison">
                    <div className="density-card success">
                      <div className="density-number">90/cm²</div>
                      <div className="density-label">Densité SAPHORÏ®</div>
                    </div>
                    <div className="density-card warning">
                      <div className="density-number">Variable</div>
                      <div className="density-label">Densité DHI*</div>
                    </div>
                  </div>

                  <p className="ben-note">
                    💡 Besoin de conseils personnalisés ? Je suis disponible 24h/24 pour analyser votre cas spécifique !
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Opinion */}
      <section className="section section-primary">
        <div className="container">
          <div className="expert-opinion">
            <div className="expert-icon">
              <Users className="w-10 h-10" />
            </div>

            <h2 className="section-title white-text">
              L'Avis de Notre Expert Capillaire
            </h2>

            <blockquote className="expert-quote">
              "SAPHORÏ® représente une évolution des techniques FUE. Cette approche optimise
              l'ensemble du processus d'implantation en personnalisant la technique selon le profil
              de chaque patient, pour une densité et une naturalité optimales."
            </blockquote>

            <div className="expert-profile">
              <div className="expert-avatar">
                <img
                  src="/team/david.jpg"
                  alt="David Benaim"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextElementSibling.style.display = 'flex'
                  }}
                />
                <div className="expert-fallback" style={{ display: 'none' }}>
                  DB
                </div>
              </div>
              <div className="expert-info">
                <p className="expert-name">David Benaim</p>
                <p className="expert-title">Expert Capillaire - CDGP</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-light">
        <div className="container">
          <div className="cta-container">
            <h2 className="section-title">
              Prêt à Découvrir SAPHORÏ® ?
            </h2>

            <p className="cta-subtitle">
              Consultation gratuite pour déterminer si SAPHORÏ® est la technique idéale pour votre cas
            </p>

            <div className="cta-buttons">
              <a href="https://www.doctolib.fr/centre-laser-et-esthetique/paris/clinique-du-grand-paris" target="_blank" rel="noopener noreferrer" className="btn">
                Consultation Gratuite SAPHORÏ®
              </a>

              <a href="/prix-greffe-cheveux-techniques-comparees" className="btn-secondary">
                Voir Tous Les Prix
              </a>
            </div>

            <div className="card cta-features">
              <div className="feature-list">
                <div className="feature-item">
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--cdgp-primary)' }} />
                  <span>Consultation gratuite</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--cdgp-primary)' }} />
                  <span>Devis personnalisé</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--cdgp-primary)' }} />
                  <span>Sans engagement</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Schema */}
      <SEOHead
        title="SAPHORÏ® vs DHI : Quelle Technique Choisir à Paris ?"
        description="Comparatif objectif entre SAPHORÏ® (CDGP) et DHI (Lutétia). Découvrez pourquoi SAPHORÏ® offre une densité supérieure et un meilleur rapport qualité-prix."
        schema={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "name": "SAPHORÏ® vs DHI : Le Comparatif",
          "description": "Comparatif technique détaillé entre la greffe SAPHORÏ® et DHI.",
          "provider": {
            "@type": "MedicalOrganization",
            "name": "Clinique du Grand Paris",
            "url": "https://bonjour.cliniquedugrandparis.com/"
          },
          "about": [
            {
              "@type": "MedicalProcedure",
              "name": "Greffe Capillaire SAPHORÏ®",
              "description": "Technique exclusive CDGP combinant Saphir et CHOÏ pour une densité maximale."
            },
            {
              "@type": "MedicalProcedure",
              "name": "Greffe Capillaire DHI",
              "description": "Technique d'implantation directe."
            }
          ]
        }}
      />
    </div>
  )
}

export default SaphoriVsDhiPage